/**
 * Fetch Actions - Data fetching and cache management for snippets
 *
 * These actions handle data fetching and cache management.
 * They interact with the blockServiceClient to make API calls and update the store.
 *
 * Key behaviors:
 * - fetchSnippets: Implements request deduplication via isLoaded/isLoading flags
 * - clearCache: Resets the store to allow fresh data on next fetch
 *
 * For snippet creation with UI feedback, see createActions.ts
 */
import type { StoreActionApi } from 'react-sweet-state';

import * as BlockServiceClient from '../../services/blockServiceClient.ts';
import { clearSnippetsCache } from '../../services/snippetsCache.ts';
import { reportError } from '../../reportError.ts';
import { ATLASSIAN_ACCOUNT_ID, type Snippet } from '../../snippetTypes.ts';

import type { SnippetsState } from '../snippetsStore.types.ts';
import { invokePendingLoadCallbacks } from '../snippetsStore.ts';

export type FetchPerformanceMetrics = {
	snippetCount: number;
	userScopeCount: number;
	globalScopeCount: number;
	payloadSizeBytes: number;
	durationMs: number;
};

/**
 * Module-level callback for fetch performance metrics.
 * The host (React layer with analytics) can register a callback via setOnFetchComplete
 * to receive performance data after each successful fetch.
 */
let onFetchCompleteCallback: ((metrics: FetchPerformanceMetrics) => void) | null = null;

/**
 * Registers a callback that receives performance metrics after each successful fetch.
 * Call this from a React component/hook that has access to useAnalyticsEvents.
 */
export const setOnFetchComplete = (
	callback: ((metrics: FetchPerformanceMetrics) => void) | null,
): void => {
	onFetchCompleteCallback = callback;
};

/**
 * Handles error reporting for fetch actions.
 * Sets fetchError state, reports to monitoring, and re-throws.
 */
const handleError = (
	error: unknown,
	setState: StoreActionApi<SnippetsState>['setState'],
	resetIsLoaded = true,
) => {
	const err = error instanceof Error ? error : new Error(String(error));
	setState({
		isLoading: false,
		fetchError: err,
		...(resetIsLoaded && { isLoaded: false }),
	});

	reportError(err);

	throw err;
};

/**
 * Actions for fetching and caching snippets data.
 */
export const fetchActions = {
	/**
	 * Fetches snippets from the server.
	 *
	 * Implements request deduplication: if snippets are already loaded (isLoaded=true)
	 * or a request is in progress (isLoading=true), returns cached data immediately.
	 * This allows multiple components to call fetchSnippets() without causing
	 * redundant API requests.
	 *
	 * @returns Promise resolving to the snippets array
	 */
	fetchSnippets:
		() =>
		async ({ getState, setState }: StoreActionApi<SnippetsState>) => {
			const { isLoaded, isLoading } = getState();

			// Request deduplication: return cached data if already loaded or currently loading
			if (isLoaded || isLoading) {
				return getState().snippets;
			}

			setState({ isLoading: true, fetchError: null });

			try {
				const fetchStart = performance.now();
				const snippets = await BlockServiceClient.fetchSnippets();
				const durationMs = Math.round(performance.now() - fetchStart);

				setState({
					snippets,
					isLoading: false,
					fetchError: null,
					isLoaded: true,
				});

				// Fire performance metrics callback if registered
				if (onFetchCompleteCallback) {
					const globalScopeCount = snippets.filter(
						(s: Snippet) => s.createdBy.accountId === ATLASSIAN_ACCOUNT_ID,
					).length;
					onFetchCompleteCallback({
						snippetCount: snippets.length,
						userScopeCount: snippets.length - globalScopeCount,
						// TODO
						globalScopeCount,
						payloadSizeBytes: new Blob([JSON.stringify(snippets)]).size,
						durationMs,
					});
				}

				// Invoke any one-time callbacks registered while this fetch was in progress
				invokePendingLoadCallbacks(snippets);

				return snippets;
			} catch (thrownError) {
				return handleError(thrownError, setState, true);
			}
		},

	/**
	 * Clears both the service cache and store state.
	 *
	 * Use this when you need to force a fresh fetch on the next request,
	 * such as after external changes to snippets data.
	 */
	clearCache:
		() =>
		({ setState }: StoreActionApi<SnippetsState>) => {
			clearSnippetsCache();
			setState({
				snippets: [],
				isLoaded: false,
			});
		},
};
